import React, { useCallback, useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from "@mui/material";
import { RiDeleteBinLine, RiPencilLine } from "@remixicon/react";
import { countStaffNotes, deleteStaffNote, getStaffNotesPage, NOTES_PAGE_SIZE } from "../../services/staffNotes";
import { isAdminRole } from "../../config/permissions";
import { dateTimeLines, showingLabel } from "../../utils/kitPresentation";
import { contactChips, noteType } from "../../utils/staffNotesPresentation";
import { tokens } from "../../utils/figmaTokens";
import { TableCard } from "../kit/SectionCard";
import Pager from "../kit/Pager";
import InitialsAvatar from "../kit/InitialsAvatar";
import StatusChip from "../kit/StatusChip";
import { RowAction } from "../kit/TableParts";
import { EmptyState, ErrorState } from "../kit/States";
import { usePagedQuery } from "../../pages/Students/tabs/usePagedQuery";
import AddNoteDialog from "./AddNoteDialog";

// The Notes pill's count (spec 059), re-read whenever `version` moves: the page bumps it after
// the head's Add note, the tab after its own add or delete, and the tab's list reloads on the
// same number. null draws no pill until the count lands.
export const useNotesCount = (uid) => {
  const [count, setCount] = useState(null);
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let cancelled = false;
    countStaffNotes(uid)
      .then((n) => !cancelled && setCount(n))
      .catch((e) => console.error("Error counting staff notes:", e));
    return () => {
      cancelled = true;
    };
  }, [uid, version]);
  const bump = useCallback(() => setVersion((v) => v + 1), []);
  return { count, version, bump };
};

// "8 Sep 2026, 18:30", or "" without a date (a write still pending).
const whenText = (value) => {
  const lines = dateTimeLines(value);
  return lines ? `${lines.date}, ${lines.time}` : "";
};

// One note: who, when, the text with its line breaks, and the actions this viewer may take. A
// contact note (D8) adds its label and chips; a note with no type is a general one.
export const NoteItem = ({ note, canEdit, canDelete, onEdit, onDelete }) => {
  const chips = contactChips(note);
  return (
    <Box component="li" sx={{ display: "flex", gap: "12px", p: "16px", borderTop: `1px solid ${tokens.border.default}` }}>
      <InitialsAvatar name={note.authorName} colorKey={note.authorUid} size={32} />
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "6px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <Typography variant="bodySmallStrong" sx={{ color: tokens.text.primary }}>
            {note.authorName || "Unknown staff"}
          </Typography>
          <Typography variant="labelMedium" component="span" sx={{ color: tokens.text.tertiary }}>
            {whenText(note.createdAt) || "Just now"}
            {note.updatedAt && " · edited"}
          </Typography>
        </Box>
        {noteType(note) === "contact" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
            <Typography variant="labelMedium" component="span" sx={{ color: tokens.text.secondary, fontWeight: 600 }}>
              Contact
            </Typography>
            {chips.map((c) => (
              <StatusChip key={c.label} label={c.label} color={c.color} />
            ))}
          </Box>
        )}
        {note.text && (
          <Typography variant="bodySmall" sx={{ color: tokens.text.primary, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
            {note.text}
          </Typography>
        )}
      </Box>
      {(canEdit || canDelete) && (
        <Box sx={{ display: "flex", gap: "2px", alignSelf: "flex-start" }}>
          {canEdit && <RowAction Icon={RiPencilLine} label="Edit note" onClick={onEdit} />}
          {canDelete && <RowAction Icon={RiDeleteBinLine} label="Delete note" onClick={onDelete} />}
        </Box>
      )}
    </Box>
  );
};

// The list card, shared by the student and tutor pages (spec 059). Newest first, 20 a page.
// Every staff member reads all notes; the author edits and deletes their own; an admin deletes
// anyone's (the rules say the same). `version` reloads the list; `onChanged` tells the page a
// note was added or removed so the pill recounts.
export const NotesCard = ({ uid, actingUser, version, onChanged, notify }) => {
  const fetchPage = useCallback((cursor) => getStaffNotesPage(uid, { cursor, pageSize: NOTES_PAGE_SIZE }), [uid]);
  const notes = usePagedQuery(fetchPage, version, "Failed to load the notes.");
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const me = actingUser?.uid || "";
  const admin = isAdminRole(actingUser?.role);

  const closeDelete = () => {
    if (deleteBusy) return;
    setDeleting(null);
    setDeleteError("");
  };
  const confirmDelete = async () => {
    setDeleteBusy(true);
    setDeleteError("");
    try {
      await deleteStaffNote(uid, deleting.id);
      setDeleting(null);
      onChanged();
      if (notify) notify("Note deleted.");
    } catch (e) {
      console.error("Error deleting the note:", e);
      setDeleteError(e.message || "Failed to delete the note.");
    } finally {
      setDeleteBusy(false);
    }
  };

  const settled = !notes.loading && !notes.error;
  return (
    <TableCard
      title="Notes"
      subtitle="Staff only · newest first · the person never sees these"
      footer={
        <Pager
          label={settled ? showingLabel(notes.page, notes.rows.length, NOTES_PAGE_SIZE) : ""}
          page={notes.page}
          hasMore={notes.hasMore}
          onPage={notes.load}
        />
      }
    >
      {notes.loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={22} />
        </Box>
      ) : notes.error ? (
        <ErrorState title={notes.error} onRetry={() => notes.load(notes.page)} />
      ) : notes.rows.length === 0 ? (
        <EmptyState title="No notes yet." message="Use Add note above to write the first one." />
      ) : (
        <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
          {notes.rows.map((note) => {
            const own = Boolean(me) && note.authorUid === me;
            return (
              <NoteItem
                key={note.id}
                note={note}
                canEdit={own}
                canDelete={own || admin}
                onEdit={() => setEditing(note)}
                onDelete={() => setDeleting(note)}
              />
            );
          })}
        </Box>
      )}

      <AddNoteDialog
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        personUid={uid}
        actingUser={actingUser}
        note={editing}
        onSaved={(message) => {
          notes.load(notes.page);
          if (notify) notify(message);
        }}
      />

      <Dialog open={Boolean(deleting)} onClose={closeDelete} maxWidth="xs" fullWidth>
        <DialogTitle style={{ fontWeight: 600 }}>Delete note</DialogTitle>
        <DialogContent dividers>
          {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
          <Typography variant="body2">
            {deleting?.authorName ? `${deleting.authorName}'s note` : "This note"} is deleted for every staff member. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDelete} disabled={deleteBusy}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={deleteBusy}>
            {deleteBusy ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </TableCard>
  );
};

// The route element for /students/:id/notes and /tutors/:id/notes: the person is whichever
// the page put in the outlet context.
const NotesTab = () => {
  const { student, tutor, actingUser, notesVersion, onNotesChanged, notify } = useOutletContext();
  const person = student || tutor;
  return <NotesCard uid={person.id} actingUser={actingUser} version={notesVersion} onChanged={onNotesChanged} notify={notify} />;
};

export default NotesTab;
